'use client'

import { useState } from 'react'

interface GlobalMarket {
  id: string
  name: string
  cap: number
  color: string
  source: string
}

interface CryptoDominance {
  btc: number
  eth: number
  stablecoins: number
  meme: number
  other: number
  totalCap: number
}

interface Props {
  globalMarkets: GlobalMarket[]
  cryptoCap: number
  dominance: CryptoDominance
}

function fmtT(v: number): string {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  return `$${(v / 1e6).toFixed(0)}M`
}

// ─── A) Proportional Squares — hero visual ──────────────────
function ProportionalSquares({ markets, cryptoCap }: { markets: GlobalMarket[]; cryptoCap: number }) {
  return (
    <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 7' }}>
      <svg viewBox="0 0 800 350" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {/* Bonds — largest */}
        <rect x="10" y="10" width="300" height="300" rx="3"
          fill="#60a5fa" fillOpacity="0.08" stroke="#60a5fa" strokeOpacity="0.3" strokeWidth="1" />
        <text x="160" y="150" textAnchor="middle" fill="#60a5fa" fillOpacity="0.6" fontSize="11" fontWeight="600" fontFamily="Saira">Global bonds</text>
        <text x="160" y="170" textAnchor="middle" fill="#60a5fa" fillOpacity="0.8" fontSize="16" fontWeight="700" fontFamily="Saira">$127T</text>
        {/* Equities */}
        <rect x="320" y="10" width="280" height="280" rx="3"
          fill="#a78bfa" fillOpacity="0.08" stroke="#a78bfa" strokeOpacity="0.3" strokeWidth="1" />
        <text x="460" y="140" textAnchor="middle" fill="#a78bfa" fillOpacity="0.6" fontSize="11" fontWeight="600" fontFamily="Saira">Global equities</text>
        <text x="460" y="160" textAnchor="middle" fill="#a78bfa" fillOpacity="0.8" fontSize="16" fontWeight="700" fontFamily="Saira">$115T</text>
        {/* S&P 500 */}
        <rect x="610" y="10" width="180" height="180" rx="3"
          fill="#f59e0b" fillOpacity="0.08" stroke="#f59e0b" strokeOpacity="0.3" strokeWidth="1" />
        <text x="700" y="95" textAnchor="middle" fill="#f59e0b" fillOpacity="0.6" fontSize="10" fontWeight="600" fontFamily="Saira">S&P 500</text>
        <text x="700" y="112" textAnchor="middle" fill="#f59e0b" fillOpacity="0.8" fontSize="14" fontWeight="700" fontFamily="Saira">$61T</text>
        {/* Gold */}
        <rect x="610" y="200" width="110" height="110" rx="3"
          fill="#eab308" fillOpacity="0.08" stroke="#eab308" strokeOpacity="0.3" strokeWidth="1" />
        <text x="665" y="252" textAnchor="middle" fill="#eab308" fillOpacity="0.6" fontSize="10" fontWeight="600" fontFamily="Saira">Gold</text>
        <text x="665" y="268" textAnchor="middle" fill="#eab308" fillOpacity="0.8" fontSize="13" fontWeight="700" fontFamily="Saira">$35T</text>
        {/* Crypto — tiny pulsing green square */}
        <rect x="320" y="300" width="14" height="14" rx="1"
          fill="#22c55e" fillOpacity="0.4" stroke="#22c55e" strokeWidth="1.5" />
        {/* Glow pulse */}
        <rect x="316" y="296" width="22" height="22" rx="2"
          fill="none" stroke="#22c55e" strokeWidth="1">
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
          <animate attributeName="x" values="316;312;316" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="296;292;296" dur="2s" repeatCount="indefinite" />
          <animate attributeName="width" values="22;30;22" dur="2s" repeatCount="indefinite" />
          <animate attributeName="height" values="22;30;22" dur="2s" repeatCount="indefinite" />
        </rect>
        {/* Label */}
        <text x="344" y="308" fill="#22c55e" fontSize="12" fontWeight="700" fontFamily="Saira">
          Crypto {fmtT(cryptoCap)}
        </text>
        <text x="344" y="322" fill="#4ade80" fontSize="9" fontWeight="500" fontFamily="Saira" fillOpacity="0.8">
          ← You are here
        </text>
      </svg>
    </div>
  )
}

// ─── E) Multiplier Callouts — proportional bars + commentary ─

function MultiplierRows({ markets, cryptoCap }: { markets: GlobalMarket[]; cryptoCap: number }) {
  const sorted = [...markets].sort((a, b) => a.cap - b.cap)
  const maxCap = sorted[sorted.length - 1].cap
  const cryptoPct = (cryptoCap / maxCap) * 100
  const cryptoVsGold = sorted.find(m => m.id === 'gold')
  const cryptoVsEquities = sorted.find(m => m.id === 'equities')

  const goldMultiple = cryptoVsGold ? (cryptoVsGold.cap / cryptoCap).toFixed(1) : '—'
  const equitiesMultiple = cryptoVsEquities ? (cryptoVsEquities.cap / cryptoCap).toFixed(0) : '—'
  const cryptoShareOfGold = cryptoVsGold ? ((cryptoCap / cryptoVsGold.cap) * 100).toFixed(1) : '—'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
      {/* LEFT: Proportional bars */}
      <div className="border border-zinc-800 px-4 py-4 self-start">
        <div className="flex flex-col">
          {/* Crypto baseline — tiny bar */}
          <div
            className="grid items-center gap-3 py-[10px] border-b border-zinc-800/40"
            style={{ gridTemplateColumns: '80px 1fr 64px' }}
          >
            <span className="text-green-500 text-[13px] font-bold text-right">Crypto</span>
            <div className="relative h-[24px]">
              <div
                className="absolute left-0 top-0 bottom-0 rounded-r-sm"
                style={{
                  width: `${Math.max(cryptoPct, 1.5)}%`,
                  background: 'linear-gradient(to right, rgba(34,197,94,0.2), rgba(34,197,94,0.5))',
                  borderRight: '2px solid #22c55e',
                }}
              />
            </div>
            <span className="text-green-500 text-[13px] font-extrabold text-right tabular-nums">{fmtT(cryptoCap)}</span>
          </div>

          {/* Market rows */}
          {sorted.map((m, idx) => {
            const barPct = (m.cap / maxCap) * 100
            const multiplier = m.cap / cryptoCap

            return (
              <div
                key={m.id}
                className={`grid items-center gap-3 py-[10px] ${idx < sorted.length - 1 ? 'border-b border-zinc-800/40' : ''}`}
                style={{ gridTemplateColumns: '80px 1fr 64px' }}
              >
                <span className="text-zinc-300 text-[13px] font-semibold text-right truncate">{m.name}</span>
                <div className="relative h-[24px]">
                  <div
                    className="absolute left-0 top-0 bottom-0 rounded-r-sm"
                    style={{
                      width: `${barPct}%`,
                      background: `linear-gradient(to right, ${m.color}10, ${m.color}30)`,
                      borderRight: `2px solid ${m.color}`,
                    }}
                  />
                  <div className="absolute inset-0 flex items-center pl-2">
                  <span
                      className="text-[11px] font-extrabold"
                      style={{ color: m.color }}
                    >
                      {multiplier.toFixed(1)}×
                    </span>
                  </div>
                </div>
                <span className="text-zinc-200 text-[13px] font-bold text-right tabular-nums">{fmtT(m.cap)}</span>
              </div>
            )
          })}
        </div>

        {/* Axis */}
        <div
          className="grid items-center gap-3 mt-2 pt-2 border-t border-zinc-800/40"
          style={{ gridTemplateColumns: '80px 1fr 64px' }}
        >
          <span />
          <div className="flex justify-between">
            <span className="text-green-400 text-[8px] uppercase tracking-widest">Crypto</span>
            <span className="text-zinc-400 text-[8px] uppercase tracking-widest">Traditional markets</span>
          </div>
          <span />
        </div>
      </div>

      {/* RIGHT: Commentary sidebar — desktop */}
      <div className="hidden lg:flex flex-col border border-zinc-800 px-5 py-5 gap-4 self-start">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              Growth potential
            </span>
          </div>
          <p className="text-zinc-200 text-[13px] font-semibold leading-snug mb-1">
            We are early
          </p>
          <p className="text-zinc-400 text-[11px] leading-relaxed">
            Crypto at {fmtT(cryptoCap)} represents just {cryptoShareOfGold}% of gold's market cap. The entire crypto market is smaller than most individual traditional asset classes.
          </p>
        </div>

        <div className="border-t border-zinc-800/60 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <svg width="14" height="8" viewBox="0 0 14 8" fill="none" className="text-zinc-400">
              <path d="M5 1L1 4L5 7Z" stroke="currentColor" strokeWidth="1" />
              <path d="M9 1L13 4L9 7Z" stroke="currentColor" strokeWidth="1" />
            </svg>
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              To match gold
            </span>
          </div>
          <p className="text-zinc-300 text-[12px] leading-relaxed">
            Crypto would need a <span className="text-yellow-500 font-bold">{goldMultiple}×</span> increase to match gold's market cap — the oldest store of value in human history.
          </p>
        </div>

        <div className="border-t border-zinc-800/60 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <svg width="14" height="8" viewBox="0 0 14 8" fill="none" className="text-zinc-400">
              <path d="M5 1L1 4L5 7Z" stroke="currentColor" strokeWidth="1" />
              <path d="M9 1L13 4L9 7Z" stroke="currentColor" strokeWidth="1" />
            </svg>
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              To match equities
            </span>
          </div>
          <p className="text-zinc-300 text-[12px] leading-relaxed">
            A <span className="text-purple-400 font-bold">{equitiesMultiple}×</span> increase would put crypto on par with global stock markets. For context, crypto has grown over 100× in the last decade.
          </p>
        </div>

        <div className="border-t border-zinc-800/60 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-zinc-400 text-[8px]">◎</span>
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              Perspective
            </span>
          </div>
          <p className="text-zinc-400 text-[11px] leading-relaxed">
            Every traditional asset class was once where crypto is now. The global bond market was under $5T in the 1990s. The question isn't if crypto grows — it's how much.
          </p>
        </div>
      </div>

      {/* MOBILE: Collapsible commentary */}
      <MobileCommentary
        cryptoCap={cryptoCap}
        cryptoShareOfGold={cryptoShareOfGold}
        goldMultiple={goldMultiple}
        equitiesMultiple={equitiesMultiple}
      />
    </div>
  )
}

function MobileCommentary({ cryptoCap, cryptoShareOfGold, goldMultiple, equitiesMultiple }: {
  cryptoCap: number; cryptoShareOfGold: string; goldMultiple: string; equitiesMultiple: string
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="lg:hidden border border-zinc-800">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span className="text-zinc-200 text-[12px] font-semibold">We are early</span>
        </div>
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          className={`text-zinc-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        >
          <path d="M2 3L5 7L8 3Z" stroke="currentColor" strokeWidth="1" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-800">
          <p className="text-zinc-400 text-[11px] leading-relaxed pt-3">
            Crypto at {fmtT(cryptoCap)} represents just {cryptoShareOfGold}% of gold. The entire crypto market is smaller than most individual traditional asset classes.
          </p>
          <div className="flex gap-4">
            <div className="flex-1">
              <span className="text-yellow-500 text-[9px] uppercase tracking-widest block mb-1">To match gold</span>
              <p className="text-zinc-300 text-[11px]">
                <span className="text-yellow-500 font-bold">{goldMultiple}×</span> growth needed
              </p>
            </div>
            <div className="flex-1">
              <span className="text-purple-400 text-[9px] uppercase tracking-widest block mb-1">To match equities</span>
              <p className="text-zinc-300 text-[11px]">
                <span className="text-purple-400 font-bold">{equitiesMultiple}×</span> growth needed
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Crypto Market Composition — treemap blocks ──────────────

const COMPOSITION_SEGMENTS = [
  { key: 'btc', label: 'Bitcoin', shortLabel: 'BTC', color: '#f59e0b' },
  { key: 'eth', label: 'Ethereum', shortLabel: 'ETH', color: '#8b5cf6' },
  { key: 'stablecoins', label: 'Stablecoins', shortLabel: 'USDT+', color: '#60a5fa' },
  { key: 'meme', label: 'Meme coins', shortLabel: 'MEME', color: '#f97316' },
  { key: 'other', label: 'Other', shortLabel: 'Other', color: '#3f3f46' },
] as const

function CompositionTreemap({ dominance }: { dominance: CryptoDominance }) {
  const [hovered, setHovered] = useState<string | null>(null)

  const segments = COMPOSITION_SEGMENTS.map((s) => ({
    ...s,
    value: dominance[s.key as keyof CryptoDominance] as number,
    cap: ((dominance[s.key as keyof CryptoDominance] as number) / 100) * dominance.totalCap,
  }))

  const btc = segments[0]
  const other = segments[4]
  const eth = segments[1]
  const stables = segments[2]
  const meme = segments[3]

  return (
    <div>
      {/* Treemap grid */}
      <div
        className="grid gap-[3px]"
        style={{
          gridTemplateColumns: `${btc.value}fr ${100 - btc.value}fr`,
          gridTemplateRows: '100px 80px',
        }}
      >
        {/* BTC — spans both rows left */}
        <div
          className="row-span-2 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-200"
          style={{
            background: `linear-gradient(135deg, ${btc.color}0F, ${btc.color}28)`,
            border: `1px solid ${btc.color}30`,
            opacity: hovered && hovered !== 'btc' ? 0.4 : 1,
          }}
          onMouseEnter={() => setHovered('btc')}
          onMouseLeave={() => setHovered(null)}
        >
          <span style={{ color: btc.color }} className="text-xl sm:text-2xl font-extrabold">{btc.shortLabel}</span>
          <span style={{ color: btc.color }} className="text-sm sm:text-base font-bold opacity-70">{btc.value.toFixed(1)}%</span>
          <span style={{ color: btc.color }} className="text-[10px] sm:text-xs opacity-40 mt-0.5">{fmtT(btc.cap)}</span>
        </div>

        {/* Other — top right */}
        <div
          className="flex items-center justify-center gap-2 cursor-pointer transition-opacity duration-200"
          style={{
            background: `linear-gradient(135deg, ${other.color}12, ${other.color}28)`,
            border: `1px solid ${other.color}30`,
            opacity: hovered && hovered !== 'other' ? 0.4 : 1,
          }}
          onMouseEnter={() => setHovered('other')}
          onMouseLeave={() => setHovered(null)}
        >
          <span style={{ color: '#71717a' }} className="text-sm font-bold">{other.shortLabel}</span>
          <span style={{ color: '#52525b' }} className="text-sm">{other.value.toFixed(1)}%</span>
        </div>

        {/* Bottom right: ETH + Stables + Meme */}
        <div
          className="grid gap-[3px]"
          style={{ gridTemplateColumns: `${eth.value}fr ${stables.value}fr ${meme.value}fr` }}
        >
          <div
            className="flex flex-col items-center justify-center cursor-pointer transition-opacity duration-200"
            style={{
              background: `linear-gradient(135deg, ${eth.color}12, ${eth.color}28)`,
              border: `1px solid ${eth.color}25`,
              opacity: hovered && hovered !== 'eth' ? 0.4 : 1,
            }}
            onMouseEnter={() => setHovered('eth')}
            onMouseLeave={() => setHovered(null)}
          >
            <span style={{ color: eth.color }} className="text-xs sm:text-sm font-bold">{eth.shortLabel}</span>
            <span style={{ color: eth.color }} className="text-[10px] sm:text-xs opacity-60">{eth.value.toFixed(1)}%</span>
          </div>
          <div
            className="flex flex-col items-center justify-center cursor-pointer transition-opacity duration-200"
            style={{
              background: `linear-gradient(135deg, ${stables.color}12, ${stables.color}28)`,
              border: `1px solid ${stables.color}25`,
              opacity: hovered && hovered !== 'stablecoins' ? 0.4 : 1,
            }}
            onMouseEnter={() => setHovered('stablecoins')}
            onMouseLeave={() => setHovered(null)}
          >
            <span style={{ color: stables.color }} className="text-[10px] sm:text-xs font-bold">{stables.shortLabel}</span>
            <span style={{ color: stables.color }} className="text-[9px] sm:text-[10px] opacity-60">{stables.value.toFixed(1)}%</span>
          </div>
          <div
            className="flex flex-col items-center justify-center cursor-pointer transition-opacity duration-200"
            style={{
              background: `linear-gradient(135deg, ${meme.color}12, ${meme.color}28)`,
              border: `1px solid ${meme.color}25`,
              opacity: hovered && hovered !== 'meme' ? 0.4 : 1,
            }}
            onMouseEnter={() => setHovered('meme')}
            onMouseLeave={() => setHovered(null)}
          >
            <span style={{ color: meme.color }} className="text-[9px] sm:text-[10px] font-bold">{meme.shortLabel}</span>
            <span style={{ color: meme.color }} className="text-[8px] sm:text-[9px] opacity-60">{meme.value.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Hover detail bar */}
      {hovered && (
        <div className="mt-3 flex items-center gap-3 px-3 py-2 border border-zinc-800/60 bg-zinc-900/30">
          {(() => {
            const seg = segments.find(s => s.key === hovered)
            if (!seg) return null
            return (
              <>
                <div className="w-3 h-3 flex-shrink-0" style={{ background: seg.color }} />
                <span className="text-zinc-200 text-[13px] font-semibold">{seg.label}</span>
                <span className="text-zinc-200 text-[13px] font-bold tabular-nums ml-auto">{seg.value.toFixed(1)}%</span>
                <span className="text-zinc-400 text-[11px] tabular-nums">{fmtT(seg.cap)}</span>
              </>
            )
          })()}
        </div>
      )}

      {/* Legend — when nothing hovered */}
      {!hovered && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 px-1">
          {segments.map((seg) => (
            <div key={seg.key} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 flex-shrink-0" style={{ background: seg.color }} />
              <span className="text-zinc-400 text-[11px]">{seg.label}</span>
              <span className="text-zinc-300 text-[11px] font-semibold tabular-nums">{seg.value.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────

export default function DominanceCharts({ globalMarkets, cryptoCap, dominance }: Props) {
  return (
    <div className="space-y-12 lg:space-y-16">
      {/* ═══ GLOBAL MARKET COMPARISON ═══ */}
      <section>
        <h2
          className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-2"
          style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
        >
          Crypto vs global markets
        </h2>
        <p className="text-zinc-400 text-sm mb-6">
          How cryptocurrency compares to traditional asset classes by total market capitalization.
        </p>

        {/* A — Proportional squares hero */}
        <div className="border border-zinc-800 mb-4">
          <ProportionalSquares markets={globalMarkets} cryptoCap={cryptoCap} />
        </div>

        {/* E — Multiplier callouts + commentary */}
        <div>
          <MultiplierRows markets={globalMarkets} cryptoCap={cryptoCap} />
        </div>
      </section>

      {/* ═══ CRYPTO INTERNAL DOMINANCE ═══ */}
      <section>
        <h2
          className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-2"
          style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
        >
          Crypto market composition
        </h2>
        <p className="text-zinc-400 text-sm mb-6">
          How the total crypto market cap is distributed across Bitcoin, Ethereum, stablecoins, meme coins, and everything else.
        </p>
        <div className="border border-zinc-800 px-4 py-4">
          <CompositionTreemap dominance={dominance} />
        </div>
      </section>
    </div>
  )
}